// Offertmallen (designen Admin-Varumarke, ADR 0067): logga, accentfärg,
// typsnitt, ton och texter. Layouten är fast så att offerten alltid blir
// komplett. Förhandsgranskningen är den riktiga offertsidan med en
// exempeloffert och det som byrån håller på att ändra.
import { accentContrast } from '@leadledger/domain/contrast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ApiFel, data } from '../api/client.js';
import type { components } from '../api/schema.js';
import { useAuth, useMe } from '../auth/auth.js';
import { Sida } from '../layout/Sida.js';

type Branding = components['schemas']['Branding'];

const FARGER = [
  { namn: 'Marinblå', hex: '#23406B' },
  { namn: 'Grön', hex: '#1E5E46' },
  { namn: 'Tegel', hex: '#8A3B2E' },
  { namn: 'Grafit', hex: '#3F3F46' },
];

const TYPSNITT: { kod: Branding['fontPair']; namn: string }[] = [
  { kod: 'sans', namn: 'Modern sans' },
  { kod: 'serif', namn: 'Klassisk serif' },
  { kod: 'neutral', namn: 'Neutral' },
];

const TONER: { kod: Branding['tone']; namn: string }[] = [
  { kod: 'formal', namn: 'Formell' },
  { kod: 'personal', namn: 'Personlig' },
  { kod: 'relaxed', namn: 'Avslappnad' },
];

const giltigFarg = (hex: string) => /^#[0-9a-fA-F]{6}$/.test(hex);

/** Texten om färgens kontrast, som i designen. */
export function kontrastText(hex: string): string {
  if (!giltigFarg(hex)) return 'Skriv färgen som #23406B.';
  const k = accentContrast(hex);
  const forhallande = k.ratio.toLocaleString('sv-SE', { maximumFractionDigits: 1 });
  return k.passesAA
    ? `Vit text på färgen är lätt att läsa (${forhallande}:1).`
    : `Färgen är ljus (${forhallande}:1 mot vitt). Knappar får mörk text och länkar en mörkare nyans, så att offerten går att läsa.`;
}

/** Tomma texter skickas inte, eftersom fälten är valfria. */
function rensa(b: Branding): Branding {
  const ut: Record<string, unknown> = {};
  for (const [nyckel, varde] of Object.entries(b)) {
    if (typeof varde === 'string' && varde.trim() === '') continue;
    ut[nyckel] = typeof varde === 'string' ? varde.trim() : varde;
  }
  return ut as Branding;
}

function Avsnitt({ rubrik, children }: { rubrik: string; children: ReactNode }) {
  const id = useId();
  return (
    <section className="avsnitt" aria-labelledby={id}>
      <h2 id={id} className="avsnitt-rubrik">
        {rubrik}
      </h2>
      {children}
    </section>
  );
}

export function Offertmall() {
  const me = useMe();
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const kanAndra = me.user.role === 'owner';
  const id = useId();

  const installningar = useQuery({
    queryKey: ['installningar'],
    queryFn: async () => data(await api.GET('/v1/admin/branding')),
  });

  const [b, setB] = useState<Branding | null>(null);
  const [kvitto, setKvitto] = useState(false);
  useEffect(() => {
    if (installningar.data && b === null) setB(installningar.data.branding);
  }, [installningar.data, b]);

  const satt = (nytt: Partial<Branding>) => {
    setKvitto(false);
    setB((g) => (g ? { ...g, ...nytt } : g));
  };

  const spara = useMutation({
    mutationFn: async (branding: Branding) =>
      data(await api.PUT('/v1/admin/branding', { body: { branding: rensa(branding) } })),
    onSuccess: (svar) => {
      queryClient.setQueryData(['installningar'], svar);
      setKvitto(true);
    },
  });

  if (installningar.isPending || !b) {
    return (
      <Sida titel="Offertmall">
        <p role="status">Hämtar offertmallen …</p>
      </Sida>
    );
  }
  if (installningar.isError) {
    return (
      <Sida titel="Offertmall">
        <div className="meddelande" role="alert">
          Offertmallen gick inte att hämta. Ladda om sidan och försök igen.
        </div>
      </Sida>
    );
  }

  const fargOk = giltigFarg(b.accentColor);

  function skicka(e: FormEvent) {
    e.preventDefault();
    if (b && fargOk) spara.mutate(b);
  }

  return (
    <Sida titel="Offertmall">
      <div className="katalog-huvud">
        <p className="ingress">
          Layouten är fast så att offerten alltid blir komplett. Logga, färg, typsnitt, ton och
          texter är era.
        </p>
      </div>
      {!kanAndra && (
        <div className="kort notis" role="note">
          Bara byråns ägare kan ändra offertmallen. Du kan se hur offerten ser ut.
        </div>
      )}
      <div className="offertmall">
        <form className="kort flikkort" onSubmit={skicka} noValidate>
          <Logga kanAndra={kanAndra} har={installningar.data.logo !== null} />
          <fieldset className="panel-falt" disabled={!kanAndra || spara.isPending}>
            <legend className="visuellt-dold">Offertmallen</legend>
            <Avsnitt rubrik="Accentfärg">
              <div className="farger">
                {FARGER.map((f) => {
                  const vald = b.accentColor.toUpperCase() === f.hex;
                  return (
                    <button
                      key={f.hex}
                      type="button"
                      className={`fargruta${vald ? ' vald' : ''}`}
                      style={{ background: f.hex }}
                      aria-label={f.namn}
                      aria-pressed={vald}
                      onClick={() => satt({ accentColor: f.hex })}
                    />
                  );
                })}
                <div className="egen-farg">
                  <label className="hjalp" htmlFor={`${id}-hex`}>
                    Egen
                  </label>
                  <input
                    id={`${id}-hex`}
                    className="in mono"
                    value={b.accentColor}
                    aria-invalid={fargOk ? undefined : true}
                    aria-describedby={`${id}-kontrast`}
                    onChange={(e) => satt({ accentColor: e.target.value })}
                  />
                </div>
              </div>
              <div className={fargOk ? 'hjalp' : 'faltfel'} id={`${id}-kontrast`}>
                {kontrastText(b.accentColor)}
              </div>
            </Avsnitt>
            <Avsnitt rubrik="Typsnitt">
              <Segment
                namn="typsnitt"
                etikett="Typsnitt"
                val={TYPSNITT}
                varde={b.fontPair}
                onVal={(fontPair) => satt({ fontPair })}
              />
            </Avsnitt>
            <Avsnitt rubrik="Ton i texterna">
              <Segment
                namn="ton"
                etikett="Ton"
                val={TONER}
                varde={b.tone}
                onVal={(tone) => satt({ tone })}
              />
              <div className="falt">
                <label className="etikett" htmlFor={`${id}-ton`}>
                  Beskriv er ton <span className="hjalp">(valfritt)</span>
                </label>
                <textarea
                  id={`${id}-ton`}
                  className="in"
                  rows={2}
                  maxLength={500}
                  value={b.toneDescription ?? ''}
                  onChange={(e) => satt({ toneDescription: e.target.value })}
                />
              </div>
            </Avsnitt>
            <Avsnitt rubrik="Texter">
              <div className="falt">
                <label className="etikett" htmlFor={`${id}-inte`}>
                  Det här ingår inte
                </label>
                <textarea
                  id={`${id}-inte`}
                  className="in"
                  rows={3}
                  maxLength={2000}
                  value={b.notIncluded ?? ''}
                  onChange={(e) => satt({ notIncluded: e.target.value })}
                />
              </div>
              <div className="falt">
                <label className="etikett" htmlFor={`${id}-villkor`}>
                  Villkor
                </label>
                <textarea
                  id={`${id}-villkor`}
                  className="in"
                  rows={2}
                  maxLength={2000}
                  value={b.terms ?? ''}
                  onChange={(e) => satt({ terms: e.target.value })}
                />
              </div>
              <div className="falt">
                <label className="etikett" htmlFor={`${id}-fot`}>
                  Sidfot
                </label>
                <input
                  id={`${id}-fot`}
                  className="in"
                  maxLength={300}
                  value={b.footer ?? ''}
                  onChange={(e) => satt({ footer: e.target.value })}
                />
              </div>
            </Avsnitt>
          </fieldset>
          {spara.isError && (
            <div className="meddelande" role="alert">
              {spara.error instanceof ApiFel && spara.error.status === 400
                ? 'Något fält är inte giltigt. Kontrollera färgen och texternas längd.'
                : 'Det gick inte att spara. Försök igen om en stund.'}
            </div>
          )}
          <div className="levande" aria-live="polite">
            {kvitto && <div className="kvitto">Offertmallen är sparad. Nya offerter får den.</div>}
          </div>
          {kanAndra && (
            <div className="panel-fot slut">
              <button type="submit" className="btn btn-p" disabled={spara.isPending || !fargOk}>
                {spara.isPending ? 'Sparar …' : 'Spara'}
              </button>
            </div>
          )}
        </form>
        <Forhandsgranskning branding={b} giltig={fargOk} />
      </div>
    </Sida>
  );
}

function Segment<T extends string>({
  namn,
  etikett,
  val,
  varde,
  onVal,
}: {
  namn: string;
  etikett: string;
  val: { kod: T; namn: string }[];
  varde: T;
  onVal: (kod: T) => void;
}) {
  return (
    <div className="segment" role="radiogroup" aria-label={etikett}>
      {val.map((v) => (
        <label key={v.kod} className={varde === v.kod ? 'valt' : undefined}>
          <input type="radio" name={namn} checked={varde === v.kod} onChange={() => onVal(v.kod)} />
          {v.namn}
        </label>
      ))}
    </div>
  );
}

/** Loggan: visa, byt eller ta bort. Sparas direkt, som filer brukar. */
function Logga({ kanAndra, har }: { kanAndra: boolean; har: boolean }) {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const fil = useRef<HTMLInputElement>(null);
  const [fel, setFel] = useState<string | null>(null);

  const bild = useQuery({
    queryKey: ['logga', har],
    enabled: har,
    queryFn: async () => {
      const svar = await api.GET('/v1/admin/branding/logo', { parseAs: 'blob' });
      if (!svar.data) throw new ApiFel(svar.response.status);
      return URL.createObjectURL(svar.data as Blob);
    },
  });

  const uppdatera = async () => {
    await queryClient.invalidateQueries({ queryKey: ['installningar'] });
    await queryClient.invalidateQueries({ queryKey: ['logga'] });
    await queryClient.invalidateQueries({ queryKey: ['forhandsgranskning'] });
  };

  const ladda = useMutation({
    mutationFn: async (f: File) => {
      const svar = await api.PUT('/v1/admin/branding/logo', {
        body: f as unknown as string,
        bodySerializer: (b: unknown) => b as BodyInit,
        headers: { 'Content-Type': f.type },
      });
      if (svar.error) {
        const detalj = (svar.error as { detail?: string }).detail;
        throw new Error(detalj ?? 'Loggan gick inte att spara.');
      }
    },
    onSuccess: async () => {
      setFel(null);
      await uppdatera();
    },
    onError: (err) => setFel(err instanceof Error ? err.message : 'Loggan gick inte att spara.'),
  });

  const taBort = useMutation({
    mutationFn: async () => {
      await api.DELETE('/v1/admin/branding/logo');
    },
    onSuccess: uppdatera,
  });

  return (
    <Avsnitt rubrik="Logga">
      <div className="logga-rad">
        <div className="logga-ruta">
          {har && bild.data ? (
            <img src={bild.data} alt="Byråns logga" />
          ) : (
            <span className="hjalp">{har ? 'Hämtar …' : 'Ingen logga'}</span>
          )}
        </div>
        <div className="hjalp">SVG eller PNG, minst 400 px bred och högst 200 kB.</div>
        {kanAndra && (
          <div className="knappar">
            <input
              ref={fil}
              type="file"
              accept="image/png,image/svg+xml"
              className="visuellt-dold"
              aria-label="Välj en logga"
              tabIndex={-1}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) ladda.mutate(f);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              className="btn btn-s"
              disabled={ladda.isPending}
              onClick={() => fil.current?.click()}
            >
              {ladda.isPending ? 'Laddar upp …' : har ? 'Byt' : 'Ladda upp'}
            </button>
            {har && (
              <button
                type="button"
                className="btn btn-g farlig"
                disabled={taBort.isPending}
                onClick={() => taBort.mutate()}
              >
                Ta bort
              </button>
            )}
          </div>
        )}
      </div>
      {fel && (
        <div className="faltfel" role="alert">
          {fel}
        </div>
      )}
    </Avsnitt>
  );
}

/** Offertsidan med exempeloffert och det osparade varumärket, i en ram utan skript. */
function Forhandsgranskning({ branding, giltig }: { branding: Branding; giltig: boolean }) {
  const { api } = useAuth();
  const [fordrojd, setFordrojd] = useState(branding);
  useEffect(() => {
    const t = setTimeout(() => setFordrojd(branding), 400);
    return () => clearTimeout(t);
  }, [branding]);

  const sida = useQuery({
    queryKey: ['forhandsgranskning', JSON.stringify(rensa(fordrojd))],
    enabled: giltig && giltigFarg(fordrojd.accentColor),
    placeholderData: (forra) => forra,
    queryFn: async () => {
      const svar = await api.POST('/v1/admin/branding/preview', {
        body: rensa(fordrojd),
        parseAs: 'text',
      });
      if (!svar.data) throw new ApiFel(svar.response.status);
      return svar.data as string;
    },
  });

  function oppnaHela() {
    if (!sida.data) return;
    const url = URL.createObjectURL(new Blob([sida.data], { type: 'text/html' }));
    window.open(url, '_blank', 'noopener');
  }

  return (
    <section className="kort flikkort forhandsgranskning" aria-label="Förhandsgranskning">
      <div className="resultat-status">
        <h2 className="resultat-rubrik">Förhandsgranskning</h2>
        <span className="hjalp">Lindqvist Snickeri AB (exempel)</span>
        <button
          type="button"
          className="btn btn-s hogerknapp"
          disabled={!sida.data}
          onClick={oppnaHela}
        >
          Förhandsgranska hela
        </button>
      </div>
      {sida.isError && (
        <div className="meddelande" role="alert">
          Förhandsgranskningen gick inte att visa.
        </div>
      )}
      {sida.data ? (
        <iframe
          className="offertram"
          title="Offerten med er offertmall"
          sandbox=""
          srcDoc={sida.data}
        />
      ) : (
        !sida.isError && <p className="hjalp">Visar offerten …</p>
      )}
    </section>
  );
}
